/*
 * Copyright (c) CIRROM 2026.
 */

import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SpellcheckOutlinedIcon from "@mui/icons-material/SpellcheckOutlined";
import {
  EmailVerdict,
  VerificationContext,
  VerifyEmailButton,
} from "./EmailVerification";
import {
  describeWriteAction,
  isEmailField,
  isEmailWriteBlocked,
  useEmailVerificationVersion,
} from "./helpers/emailVerification";
import classes from "./css/recordReview.module.css";

/*
 * Shared ad-hoc overwrite affordance, used by both the ledger and the full
 * record. The icons and copy are the modal's, so a steward who knows one
 * surface knows the other.
 */

/*
 * Fields the modal refuses to ad-hoc edit: company needs the account picker,
 * the rest are linkage/ownership fields a free-text overwrite would corrupt.
 * Note crm_id is NOT required - the payload carries it only for fields that
 * live on linked entities (addresses); the endpoint accepts null.
 *
 * The second block is account-record metadata (the account summary's
 * Overview renders these in the full record): identifiers, computed counts
 * and timestamps are facts about the record, not values a steward corrects,
 * and no adaptor write path exists for them.
 */
const AD_HOC_EXCLUDED = new Set([
  "company",
  "vip_contact",
  "contact_manager",
  "address_linkage",
  "company_type",
  "company_status",
  "account_source",
  "num_outreach",
  "num_pipeline",
  "created_time",
  "crm_account_id",
  "external_identifier",
]);

export const isAdHocEditable = (fieldName: string): boolean =>
  !AD_HOC_EXCLUDED.has(fieldName);

/**
 * Enough shape to be worth a credit. The server applies the real check; this
 * only keeps a half-typed address from being sent as a 400.
 */
export const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const AdHocEditControls = (props: {
  label: string;
  initialValue: string;
  disabled: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
  /**
   * The field being edited. An email field gets a Verify beside its write,
   * so a corrected address can be checked with Allegrow before it goes to
   * the CRM; other fields are unchanged by this.
   */
  fieldName?: string;
  verification?: VerificationContext;
}) => {
  const [value, setValue] = useState(props.initialValue);
  /*
   * The verdict belongs to the ADDRESS in the box. Change a character and the
   * old verdict no longer applies - the trigger comes back and the write is
   * a plain write again - which is exactly the right behaviour for a typo
   * being corrected. The write control reads that verdict through getters,
   * so the version hook keeps it current.
   */
  const verifiesEmail = Boolean(
    props.fieldName && isEmailField(props.fieldName)
  );
  useEmailVerificationVersion();
  const typed = value.trim();
  const writeBlocked = verifiesEmail && isEmailWriteBlocked(typed);

  const submit = () => {
    if (props.disabled || !typed || writeBlocked) {
      return;
    }
    props.onSubmit(typed);
  };

  const writeHint = verifiesEmail
    ? describeWriteAction(
        typed,
        "Push the manual overwrite to your CRM.",
        "written",
        "Correct the address to write a different one."
      )
    : "Push the manual overwrite to your CRM.";

  return (
    <div className={classes.fieldEditStack}>
      <div className={classes.fieldEditControls}>
        <TextField
          size="small"
          variant="standard"
          fullWidth
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              submit();
            }
            if (event.key === "Escape") {
              props.onCancel();
            }
          }}
        />

        <Tooltip title="Go back to the previous value.">
          <IconButton size="small" onClick={props.onCancel}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {verifiesEmail && looksLikeEmail(typed) && (
          <VerifyEmailButton
            email={typed}
            context={props.verification}
            disabled={props.disabled}
          />
        )}
        <Tooltip title={writeHint}>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={props.disabled || !typed || writeBlocked}
              aria-label={`Write ${props.label} to your CRM`}
              onClick={submit}
            >
              <SpellcheckOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {verifiesEmail && typed && (
        <EmailVerdict email={typed} context={props.verification} />
      )}
    </div>
  );
};
